import { SF_FIELDS, SF_PARTIES, SF_TITLES, omr } from "../lib/showroomForms";

import { tr } from "../lib/i18n";
// English half of the bilingual paper stays English in both modes
const PAPER_EN = { no: "No.", corresponding: "Corresponding to", important: "Important:" };

function Header({ f, s }) {
  const d = f.dates || {};
  return (
    <div data-testid="sf-doc-header">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="text-sm">
          <div className="font-semibold">{s.showroom_name}</div>
          {[s.branch, s.address, s.cr_number && `CR No. ${s.cr_number}`, [s.phone, s.email, s.website].filter(Boolean).join(" · ")].filter(Boolean).map((x) => <div key={x} className="opacity-70">{x}</div>)}
        </div>
        {s.logo_light_url && <img src={s.logo_light_url} alt={s.showroom_name || "logo"} style={{ height: 56, objectFit: "contain" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />}
        <div className="text-sm text-right" dir="rtl">
          <div className="font-semibold">{s.showroom_name_ar || s.showroom_name}</div>
          {s.address_ar && <div className="opacity-70">{s.address_ar}</div>}
          {s.cr_number && <div className="opacity-70">س.ت <span dir="ltr">{s.cr_number}</span></div>}
        </div>
      </div>
      <div className="text-center my-4">
        <div className="font-semibold tracking-wide">{SF_TITLES[f.form_type][0]}</div>
        <div className="font-semibold text-lg" dir="rtl">{SF_TITLES[f.form_type][1]}</div>
      </div>
      <div className="grid gap-2 text-sm" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <div data-testid="sf-doc-number"><b>{PAPER_EN.no}</b> <span dir="ltr">{f.reference}</span></div>
        <div>{d.day_en} <span dir="ltr">{d.date}</span></div>
        <div>{PAPER_EN.corresponding} {d.hijri_en}</div>
        <div dir="rtl">الموافق {d.hijri_ar}</div>
      </div>
    </div>
  );
}

function Value({ k, type, v }) {
  if (type === "money" || type === "ro-money") return <span dir="ltr">{omr(v)}</span>;
  return <span dir={type === "tel" ? "ltr" : undefined}>{v === undefined || v === null || v === "" ? "—" : String(v)}</span>;
}

export function FieldRows({ f, editable = {}, values, onChange, placeholders = {} }) {
  const data = values || f.data || {};
  return SF_FIELDS[f.form_type].map(([en, ar, rows]) => (
    <div key={en} className="mt-4">
      <div className="flex justify-between text-xs font-semibold uppercase opacity-70"><span>{en}</span><span dir="rtl">{ar}</span></div>
      <div className="border rounded-lg divide-y mt-1" style={{ borderColor: "var(--divider)" }}>
        {rows.map(([k, len, lar, type, req]) => (
          <div key={k} className="grid gap-1 sm:gap-2 sm:items-center px-3 py-2 text-sm sm:grid-cols-[minmax(110px,1fr)_minmax(0,1.6fr)_minmax(110px,1fr)]">
            <div className="flex justify-between gap-2 sm:contents">
              <label htmlFor={`sf-${k}`} className="opacity-70">{len}{req && editable[k] ? " *" : ""}</label>
              <div className="opacity-70 text-right sm:hidden" dir="rtl">{lar}</div>
            </div>
            {editable[k] ? (
              <input id={`sf-${k}`} data-testid={`sf-field-${k}`} value={data[k] ?? ""} placeholder={placeholders[k]} inputMode={type === "money" || type === "number" ? "decimal" : undefined}
                dir={type === "tel" ? "ltr" : "auto"} onChange={(e) => onChange(k, e.target.value)}
                className="h-11 rounded-lg border px-3 w-full" style={{ borderColor: "var(--divider)" }} />
            ) : <div data-testid={`sf-value-${k}`}><Value k={k} type={type} v={data[k]} /></div>}
            <div className="opacity-70 text-right hidden sm:block" dir="rtl">{lar}</div>
          </div>
        ))}
      </div>
    </div>
  ));
}

export function TermsBlock({ f }) {
  const tx = f.texts || {};
  const d = f.data || {};
  const fine = Number(d.fine_amount || 0).toFixed(3);
  const en = [...(tx.terms_en || [])];
  const ar = [...(tx.terms_ar || [])];
  if (f.form_type === "sale_contract") ["extra_condition_5", "extra_condition_6"].forEach((k) => { if (d[k]) { en.push(d[k]); ar.push(d[k]); } });
  return (
    <div className="mt-5 grid gap-x-6 gap-y-2 text-sm" style={{ gridTemplateColumns: "1fr 1fr" }} data-testid="sf-terms">
      {en.map((e, i) => [
        <div key={`e${i}`}>{i + 1}. {e.replace("{fine}", fine)}</div>,
        <div key={`a${i}`} dir="rtl">{i + 1}. {(ar[i] || "").replace("{fine}", fine)}</div>,
      ])}
      <div className="font-semibold">{PAPER_EN.important} {tx.remark_en}</div>
      <div className="font-semibold" dir="rtl">ملاحظة هامة: {tx.remark_ar}</div>
    </div>
  );
}

export function SignatureRow({ f }) {
  const sigs = f.signatures || {};
  return (
    <div className="mt-5 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
      {SF_PARTIES[f.form_type].map(([p, en, ar]) => (
        <div key={p} className="border rounded-lg p-3 text-sm" style={{ borderColor: "var(--divider)" }} data-testid={`sf-sig-${p}`}>
          <div className="flex justify-between font-semibold"><span>{en}</span><span dir="rtl">{ar}</span></div>
          {sigs[p]?.image ? <img src={sigs[p].image} alt={`${en} signature`} style={{ height: 70, background: "#fff" }} /> : <div className="h-[70px] flex items-center opacity-50">{sigs[p] ? "✓" : "—"}</div>}
          <div className="text-xs opacity-70">{sigs[p] ? `${sigs[p].name || ""} · ${String(sigs[p].signed_at || "").slice(0, 16).replace("T", " ")}` : ""}</div>
        </div>
      ))}
    </div>
  );
}

export default function FormDocument({ form, showroom, editable, values, onChange, hideSignatures, placeholders }) {
  return (
    <div className="panel" dir="ltr" style={{ padding: 24, background: "var(--surface, #fff)" }} data-testid="sf-document">
      <Header f={form} s={showroom || {}} />
      <FieldRows f={form} editable={editable} values={values} onChange={onChange} placeholders={placeholders} />
      <TermsBlock f={form} />
      {!hideSignatures && <SignatureRow f={form} />}
    </div>
  );
}
